import React, { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import Button from '../../components/ui/Button';
import SegmentedControl from '../../components/ui/SegmentedControl';
import {
  getDueSchedule,
  type DueScheduleCurrencyBlock,
  type DueScheduleGroupBy,
  type DueScheduleReport,
} from '../../lib/accountsPayableReportsApi';
import { downloadObjectsCsv } from '../../utils/downloadCsv';
import { schedulePeriodHref, scheduleSlotHref, type ScheduleSlot } from './drillDown';
import { billCountLabel, formatReportDay, formatReportMoney } from './format';
import { IndicativeLine, ReportFrame } from './ReportFrame';
import ReportLink from './ReportLink';
import { useReport } from './useReport';
import DateInput from "../../components/DateInput";

/**
 * Due schedule (D49 phase 2): what falls due, day by day or week by week, per currency.
 *
 * Bills already late when the range starts are "carried over" (still to pay, not lost); bills on
 * hold or disputed are owed but never scheduled (they sit in their own row); a bill with no due
 * date has its own row. Empty days and weeks are listed at zero so the range reads continuously.
 * The periods, their labels and the range are the server's: weeks run Monday to Sunday.
 */

const FIELD_INPUT =
  'h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20';

interface ScheduleLine {
  key: string;
  label: string;
  amount: number;
  count: number;
  href: string | null;
  /** The due days the line covers, where it covers a stated range (blank = open-ended or none). */
  dueFrom: string;
  dueTo: string;
  /** The line is one of the columns that add up to what is owed, drawn apart from the periods. */
  kind: 'period' | 'slot' | 'total';
}

function linesFor(block: DueScheduleCurrencyBlock): ScheduleLine[] {
  const slot = (
    key: ScheduleSlot,
    label: string,
    amount: number,
    count: number,
    range: { dueFrom?: string; dueTo?: string } = {}
  ): ScheduleLine => ({
    key,
    label,
    amount,
    count,
    href: scheduleSlotHref(block, key),
    dueFrom: range.dueFrom ?? '',
    dueTo: range.dueTo ?? '',
    kind: 'slot',
  });
  return [
    slot('carriedOver', 'Carried over: already past due when the range starts', block.carriedOver.amount, block.carriedOver.count, {
      dueTo: block.carriedOver.dueTo,
    }),
    ...block.periods.map(
      (period): ScheduleLine => ({
        key: `${period.start}`,
        label: period.label,
        amount: period.amount,
        count: period.count,
        href: schedulePeriodHref(block.currency, period),
        dueFrom: period.start,
        dueTo: period.end,
        kind: 'period',
      })
    ),
    slot('afterRange', 'Due after the range', block.afterRange.amount, block.afterRange.count, {
      dueFrom: block.afterRange.dueFrom,
    }),
    slot('noDueDate', 'No due date', block.noDueDate.amount, block.noDueDate.count),
    slot('onHoldDisputed', 'On hold or disputed (owed, not scheduled)', block.onHoldDisputed.amount, block.onHoldDisputed.count),
    {
      key: 'owed',
      label: 'Total owed',
      amount: block.totals.owed,
      count: block.totals.billCount,
      href: scheduleSlotHref(block, 'owed'),
      dueFrom: '',
      dueTo: '',
      kind: 'total',
    },
  ];
}

/** The rows of the CSV: every line of every currency's table, with the days each covers. */
export function dueScheduleCsvRows(report: DueScheduleReport): Array<Record<string, unknown>> {
  const rows: Array<Record<string, unknown>> = [];
  for (const block of report.currencies) {
    for (const line of linesFor(block)) {
      rows.push({
        Currency: block.currency,
        Line: line.label,
        'Due from': line.dueFrom,
        'Due to': line.dueTo,
        Amount: line.amount,
        Bills: line.count,
      });
    }
  }
  return rows;
}

const GROUPS = [
  { value: 'day', label: 'By day' },
  { value: 'week', label: 'By week' },
] as const;

export const DueScheduleView: React.FC = () => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [groupBy, setGroupBy] = useState<DueScheduleGroupBy>('day');

  const state = useReport(async () => {
    const res = await getDueSchedule({ from, to, groupBy });
    return res.data;
  }, [from, to, groupBy]);

  const report = state.data;
  const csvRows = useMemo(() => (report ? dueScheduleCsvRows(report) : []), [report]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">From</span>
          <DateInput validity="filter" value={from} onChange={(v) => setFrom(v)} className={FIELD_INPUT} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">To</span>
          <DateInput validity="filter" value={to} onChange={(v) => setTo(v)} className={FIELD_INPUT} />
        </label>
        <SegmentedControl
          aria-label="Group the schedule"
          value={groupBy}
          onChange={(next) => setGroupBy(next as DueScheduleGroupBy)}
          segments={GROUPS.map((g) => ({ value: g.value, label: g.label }))}
        />
        {from || to ? (
          <Button
            size="sm"
            onClick={() => {
              setFrom('');
              setTo('');
            }}
          >
            Next 30 days
          </Button>
        ) : null}
        <div className="ml-auto">
          <Button
            variant="primary"
            icon={<Download aria-hidden="true" className="h-4 w-4" />}
            disabled={csvRows.length === 0}
            onClick={() => downloadObjectsCsv(`ap-due-schedule-${report?.from ?? 'range'}-to-${report?.to ?? ''}.csv`, csvRows)}
          >
            Export CSV
          </Button>
        </div>
      </div>
      <p className="mb-4 max-w-[80ch] text-xs text-slate-600">
        {report ? `Showing ${formatReportDay(report.from)} to ${formatReportDay(report.to)}. ` : ''}
        Leave both dates empty for today and the next 30 days. Weeks run Monday to Sunday. Bills on hold or
        disputed are owed but are not scheduled: they have their own line.
      </p>

      <ReportFrame
        state={state}
        isEmpty={(data) => data.currencies.length === 0}
        emptyTitle="Nothing is owed to suppliers."
        emptyDetail="Every bill is paid or credited in full."
      >
        {(data) => (
          <>
            {data.currencies.map((block) => (
              <section key={block.currency} aria-label={`Due schedule in ${block.currency}`} className="mb-6">
                <h3 className="mb-2 text-sm font-semibold text-slate-900">{block.currency}</h3>
                <div
                  role="region"
                  aria-label={`Due schedule in ${block.currency}, scrolls sideways on a narrow screen`}
                  tabIndex={0}
                  className="relative overflow-x-auto rounded-lg border border-slate-200 bg-white"
                >
                  <table className="data-table w-full min-w-[420px] text-sm">
                    <caption className="sr-only">
                      Amounts owed in {block.currency} falling due each {data.groupBy} from{' '}
                      {formatReportDay(data.from)} to {formatReportDay(data.to)}.
                    </caption>
                    <thead className="border-b border-slate-200 bg-slate-50">
                      <tr>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-slate-600">
                          Due
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-xs font-semibold text-slate-600">
                          Amount
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-xs font-semibold text-slate-600">
                          Bills
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {linesFor(block).map((line) => {
                        const money = formatReportMoney(line.amount, block.currency);
                        return (
                          <tr
                            key={`${line.kind}-${line.key}`}
                            className={
                              line.kind === 'total'
                                ? 'border-t-2 border-slate-300 bg-slate-50 font-semibold'
                                : line.kind === 'slot'
                                  ? 'border-b border-slate-100 bg-slate-50/50'
                                  : 'border-b border-slate-100'
                            }
                          >
                            <th scope="row" className="px-3 py-2 text-left text-sm font-medium text-slate-800">
                              {line.label}
                            </th>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {line.amount && line.href ? (
                                <ReportLink href={line.href} label={`${money}, ${line.label}. Open these bills`}>
                                  {money}
                                </ReportLink>
                              ) : (
                                <span className="text-slate-400">
                                  <span aria-hidden="true">--</span>
                                  <span className="sr-only">nothing</span>
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right text-xs text-slate-600">{billCountLabel(line.count)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
            <IndicativeLine
              indicative={data.indicative}
              figureKey="owed"
              currencyCount={data.currencies.length}
              what="total owed"
            />
          </>
        )}
      </ReportFrame>
    </div>
  );
};

export default DueScheduleView;
